import { historySVC } from "app/history/service"
import { getState } from "app/state"
import { getStore } from "app/store/store"
import { getDateFnsDateFormat, getDateFnsLocale, is12HoursTimeFormat } from "app/util/date/tools/common"
import { localStorageSVC } from "app/util/local-storage/service"
import { isLoggedIn, logoutAndLoginAgain } from "user/tools/common"
import { startLanguageListeners } from "./listeners"
import {
  DEFAULT_LOCALE,
  ILanguageService,
  ITranslateParams,
  JCoreTranslateFn,
  JTranslationByLocale,
  LOCALES,
  PARAM_OVERRIDEN_LOCALE,
  USER_CHANGE_LOCALE_LOCAL_STORAGE_ID
} from "./model"
import { isValidLocale, setCurrentLocaleFromContext } from "./tools/common"

startLanguageListeners()

function getLanguage() {
  return getState().language
}

export const languageSVC: ILanguageService = {
  getLocale(): LOCALES {
    return getStore().getState().language.locale
  },
  getDateFnsLocale,
  getDefaultLocale(): LOCALES {
    return DEFAULT_LOCALE
  },
  getLocales(): LOCALES[] {
    return Object.keys(getLanguage().translationsByLocale) as LOCALES[]
  },
  async setLocale(locale: LOCALES): Promise<void> {
    if (isValidLocale(locale)) {
      if (locale !== getStore().getState().language.locale) {
        // USER_CHANGE_LOCALE_LOCAL_STORAGE_ID will be managed in setCurrentLocaleFromContext
        localStorageSVC.set(USER_CHANGE_LOCALE_LOCAL_STORAGE_ID, locale)
        // remove overriden settings
        historySVC.popHashParameters(PARAM_OVERRIDEN_LOCALE)
        if (window.APP_OPTIONS?.locale !== undefined) {
          delete window.APP_OPTIONS.locale
        }
        setCurrentLocaleFromContext()
        if (isLoggedIn()) {
          await logoutAndLoginAgain() // will reload all data with correct locale
        }
      }
    } else {
      throw Error(`invalid locale "${locale}"`)
    }
  },
  translate(translationParams: ITranslateParams): string {
    const { key, locale: paramLocale, params } = translationParams
    const defaultLocale = getLanguage().defaultLocale ?? DEFAULT_LOCALE
    const locale: string = !paramLocale ? getStore().getState().language.locale : paramLocale
    const localeTranlations = getLanguage().translationsByLocale?.[locale]
    let translation: string = localeTranlations ? localeTranlations[key] : ""
    if (!translation) {
      if (locale !== defaultLocale) {
        return languageSVC.translate({ key, locale: defaultLocale, params })
      }
      return "??-" + key + "-??"
    }
    if (params === undefined) {
      return translation
    } else if (Array.isArray(params)) {
      const matches = translation.match(/{(\d+)}/g)
      if ((!matches && params.length > 0) || (matches && matches.length !== params.length)) {
        return translation + "-??-missing-param-??"
      }
      let i = 0
      let txtPattern
      let regex
      for (; i < params.length; i++) {
        txtPattern = `{[${i}]}`
        regex = new RegExp(txtPattern)
        translation = translation.replace(regex, params[i] as string)
      }
      return translation
    } else {
      return translation.replace("{0}", params as string)
    }
  },
  getDateFnsDateFormat,
  is12HoursTimeFormat,
  isValidLocale
}

export const translate: JCoreTranslateFn = (
  key: string,
  params?: (string | number) | Array<string | number>,
  paramLocale?: LOCALES
) => languageSVC.translate({ key, params, locale: paramLocale })

export const getEmptyTranslation = (): JTranslationByLocale => ({
  [LOCALES.EN]: "",
  [LOCALES.FR]: "",
  [LOCALES.ES]: "",
  [LOCALES.AR]: ""
})
